import React, { useState, useEffect, useCallback, useContext } from 'react';
import RoomContext from '../RoomContext';
import { useRoomApi } from '../hooks/useRoomApi';
import CalibrationInput from './CalibrationInput';
import { ROLES, roleLabel, isHeater } from './deviceRoles';

// Devices bound to this room (with heater calibration), and the registry of unassigned devices that
// can be claimed into it.

const DeviceManager = ({ roomId }) => {
  const { tenant, room } = useContext(RoomContext);
  const api = useRoomApi(tenant, room);
  const [devices, setDevices] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, u] = await Promise.all([api.getDevices(), api.getUnassignedDevices()]);
      setDevices(d);
      setUnassigned(u);
      setError(null);
    } catch (e) {
      setError('No se pudieron cargar los dispositivos.');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const calibrate = async (mac, body) => {
    await api.calibrateDevice(mac, body);
    await load();
  };

  const assign = async (mac, role) => {
    try { await api.assignDevice(mac, { roomId, role }); } catch (e) { setError('No se pudo asignar.'); }
    await load();
  };

  if (error && !devices) return <div className="text-danger small">{error}</div>;
  if (!devices) return <div className="small text-muted">Cargando…</div>;

  return (
    <>
      {devices.length === 0 && <p className="small text-muted mb-2">No hay dispositivos asignados a esta sala.</p>}
      {devices.map((d) => (
        <div key={d.mac} className="mb-3 pb-2 border-bottom">
          <div className="d-flex justify-content-between">
            <span className="small fw-semibold">{roleLabel(d.role)}</span>
            <code className="small text-muted">{d.mac}</code>
          </div>
          {isHeater(d.role) && <CalibrationInput device={d} onSave={(body) => calibrate(d.mac, body)} />}
        </div>
      ))}

      <div className="mt-2">
        <span className="small fw-semibold">Sin asignar</span>
        {unassigned.length === 0 && <p className="small text-muted mb-0">No hay dispositivos sin asignar.</p>}
        {unassigned.map((d) => (
          <AssignRow key={d.mac} mac={d.mac} onAssign={assign} />
        ))}
      </div>
      {error && devices && <div className="text-danger small mt-2">{error}</div>}
    </>
  );
};

const AssignRow = ({ mac, onAssign }) => {
  const [role, setRole] = useState(ROLES[0].value);
  return (
    <div className="row g-2 align-items-center mt-1">
      <div className="col-5"><code className="small">{mac}</code></div>
      <div className="col-4">
        <select className="form-select form-select-sm" value={role} onChange={(e) => setRole(Number(e.target.value))}>
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>
      <div className="col-3">
        <button className="btn btn-outline-primary btn-sm w-100" onClick={() => onAssign(mac, role)}>Asignar</button>
      </div>
    </div>
  );
};

export default DeviceManager;
